import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Spin, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchMobileCardCounts, fetchGeofenceCardCounts, CardCount } from "../../services/NotificationDashboard";

const { Title, Text } = Typography;

interface CardData {
  key: string;
  title: string;
  description?: string;
  total?: string;
  date?: string;
  color?: string;
  image: string;
  route: string;
}

const NotificationPage: React.FC = () => {
  
  const [data, setData] = useState<CardData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    return tab === "geofence" ? "geofence" : "mobile";
  };

  const [activeTab, setActiveTab] = useState<"mobile" | "geofence">(getTabFromQuery());

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Base card structures
  const baseMobileCards: CardData[] = [
    { key: "create", title: "Create New", description: "Start a new mobile notification", image: "../images/create.png", route: "/Notification-create-mobile/create" },
    { key: "drafts", title: "Drafts", image: "../images/draft.png", route: "/Notification-mobile-list?status=0" },
    { key: "active", title: "Active", image: "../images/active.png", color: "green", route: "/Notification-mobile-list?status=1" },
    { key: "upcoming", title: "Upcoming", image: "../images/upcomming.png", color: "gold", route: "/Notification-mobile-list?status=2" },
    { key: "past", title: "Past Notifications", image: "../images/past.png", color: "red", route: "/Notification-mobile-list?status=3" },
  ];

  const baseGeofenceCards: CardData[] = [
    { key: "create", title: "Create New", description: "Start a new geofence notification", image: "../images/create.png", route: "/CreateGeofenceNotification?identifier=999&navigationTitle=Create&tab=geofence" },
    { key: "drafts", title: "Drafts", image: "../images/draft.png", route: "/ListGeofenceNotification?statusId=0&navigationTitle=Drafts Notifications&tab=geofence" },
    { key: "active", title: "Active", image: "../images/active.png", color: "green", route: "/ListGeofenceNotification?statusId=1&navigationTitle=Active Notifications&tab=geofence" },
    { key: "upcoming", title: "Upcoming", image: "../images/upcomming.png", color: "gold", route: "/ListGeofenceNotification?statusId=2&navigationTitle=Upcoming Notifications&tab=geofence" },
    { key: "past", title: "Past Notifications", image: "../images/past.png", color: "red", route: "/ListGeofenceNotification?statusId=3&navigationTitle=Past Notifications&tab=geofence" },
  ];

  const loadData = async (tab: "mobile" | "geofence") => {
    setLoading(true);
    try {
      const baseCards = tab === "mobile" ? baseMobileCards : baseGeofenceCards;
      const counts = tab === "mobile"
        ? await fetchMobileCardCounts()
        : await fetchGeofenceCardCounts();

      const cards = baseCards.map((card, index) => {
        const countData = counts.find((c) => c.key === card.key);
        const totalCount = countData?.total ?? 0;

        return {
          ...card,
          // First card never shows 0
          total: index === 0 ? "" : `Total ${card.title} : ${totalCount}`,
          date: countData?.lastDate || card.date,
          // Disable cards with 0 count except the first
          disabled: index !== 0 && totalCount === 0,
        };
      });

      setData(cards);
    } catch (err) {
      message.error("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tab = getTabFromQuery();
    setActiveTab(tab);
    loadData(tab);
  }, [location.search]);

  const handleCardClick = (card: CardData) => {
    if (card.route) navigate(card.route);
  };

  const handleTabClick = (tab: "mobile" | "geofence") => {
    navigate(`?tab=${tab}`);
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Tabs */}
      <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
        <div
          onClick={() => handleTabClick("mobile")}
          style={{ flex: 1, textAlign: "center", padding: "12px 0", background: activeTab === "mobile" ? "#41787C" : "#f5f5f5", color: activeTab === "mobile" ? "#fff" : "#000", fontWeight: 600, cursor: "pointer" }}
        >
          Mobile Notifications
        </div>
        <div
          onClick={() => handleTabClick("geofence")}
          style={{ flex: 1, textAlign: "center", padding: "12px 0", background: activeTab === "geofence" ? "#41787C" : "#f5f5f5", color: activeTab === "geofence" ? "#fff" : "#000", fontWeight: 600, cursor: "pointer" }}
        >
          Geofence Notifications
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <img
            src="/images/newLoading.gif" // <-- path to your GIF
            alt="Loading..."
            style={{ width: 200, height: 150 }}
          />
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {data.map((card) => (
            <Col xs={24} sm={12} md={8} lg={8} key={card.key}>
              <Card
                hoverable={!card.disabled}
                onClick={() => !card.disabled && handleCardClick(card)}
                cover={
                  <div style={{ height: 180, borderRadius: 8, overflow: "hidden", position: "relative" }}>
                    <img
                      src={card.image}
                      alt={card.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: card.disabled ? "grayscale(70%)" : "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: 12,
                        background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                        color: "#fff",
                      }}
                    >
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          color: card.color || "#fff",
                          fontWeight: 600,
                          opacity: card.disabled ? 0.6 : 1,
                        }}
                      >
                        {card.title}
                      </Title>
                      {card.description && (
                        <Text style={{ color: "#ddd", fontSize: 13, opacity: card.disabled ? 0.6 : 1 }}>
                          {card.description}
                        </Text>
                      )}
                      {/* 🔹 Only show total if it's NOT the first card (new notification card) */}
                      {card.total && card.key !== "new" && (
                        <div style={{ fontSize: 13, opacity: card.disabled ? 0.6 : 1 }}>{card.total}</div>
                      )}
                      {card.date && (
                        <div style={{ fontSize: 12, color: "#ccc", opacity: card.disabled ? 0.6 : 1 }}>
                          Last Saved: {formatDate(card.date)}
                        </div>
                      )}
                    </div>
                  </div>
                }
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  cursor: card.disabled ? "not-allowed" : "pointer",
                }}
                bodyStyle={{ padding: 0 }}
              />


            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default NotificationPage;

